import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle as CheckIcon,
  LocalHospital as HospitalIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Smartphone as SmartphoneIcon,
  Cloud as CloudIcon,
  Storage as StorageIcon,
  Print as PrintIcon,
  LocalPharmacy as PharmacyIcon
} from '@mui/icons-material';
import landingTheme from '../../theme/landingTheme';
import LandingLayout from '../../components/LandingLayout';

const FeaturesPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <HospitalIcon sx={{ fontSize: 60 }} />,
      title: 'Gestione Residenti Completa',
      description: 'Sistema avanzato per la gestione anagrafica e clinica degli ospiti',
      details: [
        'Anagrafica dettagliata con foto e documenti',
        'Storico medico completo con timeline',
        'Allergie, intolleranze e patologie',
        'Contatti familiari e medici di riferimento',
        'Note personalizzate e promemoria',
        'Gestione documenti e consensi'
      ],
      color: '#2e7d32'
    },
    {
      icon: <MedicalIcon sx={{ fontSize: 60 }} />,
      title: 'Terapie Farmacologiche',
      description: 'Monitoraggio completo delle somministrazioni farmacologiche',
      details: [
        'Piano terapeutico personalizzato',
        'Promemoria automatici per somministrazioni',
        'Tracking delle assunzioni con firma digitale',
        'Gestione scadenze farmaci',
        'Alert per mancate somministrazioni',
        'Storico completo delle terapie'
      ],
      color: '#c62828'
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 60 }} />,
      title: 'Calendario Visite e Appuntamenti',
      description: 'Pianificazione efficiente di visite mediche e controlli',
      details: [
        'Calendario condiviso per tutto lo staff',
        'Prenotazione visite specialistiche',
        'Promemoria automatici via email/SMS',
        'Gestione disponibilità medici',
        'Report visite effettuate',
        'Integrazione con Google Calendar'
      ],
      color: '#1565c0'
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 60 }} />,
      title: 'Gestione Staff e Turni',
      description: 'Coordinamento ottimale del personale infermieristico',
      details: [
        'Pianificazione turni con drag & drop',
        'Gestione ferie e permessi',
        'Badge elettronico con timbrature',
        'Competenze e qualifiche del personale',
        'Notifiche cambio turno',
        'Report ore lavorate per buste paga'
      ],
      color: '#f57c00'
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 60 }} />,
      title: 'Dashboard e Report',
      description: 'Analytics avanzate per decisioni data-driven',
      details: [
        'Dashboard KPI in tempo reale',
        'Report personalizzabili',
        'Grafici e statistiche dettagliate',
        'Export dati in Excel/PDF',
        'Benchmark tra periodi',
        'Analisi predittive con AI'
      ],
      color: '#7b1fa2'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 60 }} />,
      title: 'Sicurezza e Conformità GDPR',
      description: 'Massima sicurezza dei dati sensibili',
      details: [
        'Crittografia AES-256 end-to-end',
        'Autenticazione a due fattori (2FA)',
        'Log di accesso e audit trail',
        'Backup automatici giornalieri',
        'Conformità GDPR e normative sanitarie',
        'Certificazione ISO 27001'
      ],
      color: '#424242'
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 60 }} />,
      title: 'Sistema di Alert Intelligente',
      description: 'Notifiche proattive per eventi critici',
      details: [
        'Alert parametri vitali anomali',
        'Notifiche scadenze farmaci',
        'Promemoria visite mediche',
        'Allerta emergenze in tempo reale',
        'Notifiche push su smartphone',
        'Escalation automatica al responsabile'
      ],
      color: '#d32f2f'
    },
    {
      icon: <SmartphoneIcon sx={{ fontSize: 60 }} />,
      title: 'App Mobile Nativa',
      description: 'Accesso da smartphone e tablet iOS/Android',
      details: [
        'Consultazione dati ovunque',
        'Firma digitale per somministrazioni',
        'Scanner QR code per identificazione',
        'Modalità offline con sync automatica',
        'Notifiche push in tempo reale',
        'Interfaccia ottimizzata per touch'
      ],
      color: '#00796b'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 60 }} />,
      title: 'Integrazione IoT e Sensori',
      description: 'Monitoraggio automatico con dispositivi smart',
      details: [
        'Braccialetti smart per tracking',
        'Sensori ambientali (temperatura, umidità)',
        'Bilance e misuratori pressione connessi',
        'Pulsanti SOS wireless',
        'Sensori di movimento per cadute',
        'Dashboard unificata dispositivi IoT'
      ],
      color: '#0288d1'
    },
    {
      icon: <PharmacyIcon sx={{ fontSize: 60 }} />,
      title: 'Magazzino Farmaci',
      description: 'Gestione completa del magazzino farmaceutico',
      details: [
        'Inventario in tempo reale',
        'Alert giacenze minime',
        'Gestione lotti e scadenze',
        'Ordini automatici ai fornitori',
        'Tracciabilità movimento farmaci',
        'Report costi farmaceutici'
      ],
      color: '#c2185b'
    },
    {
      icon: <PrintIcon sx={{ fontSize: 60 }} />,
      title: 'Documenti e Reportistica',
      description: 'Generazione automatica documenti amministrativi',
      details: [
        'Contratti di ospitalità personalizzati',
        'Fatture elettroniche XML',
        'Certificati medici',
        'Report sanitari per ASL',
        'Consensi informati digitali',
        'Moduli personalizzabili'
      ],
      color: '#5d4037'
    },
    {
      icon: <StorageIcon sx={{ fontSize: 60 }} />,
      title: 'Archivio Digitale',
      description: 'Storage illimitato per documenti',
      details: [
        'Upload documenti illimitati',
        'OCR per ricerca testo nei PDF',
        'Versionamento automatico',
        'Condivisione sicura con familiari',
        'Organizzazione per categorie',
        'Ricerca full-text avanzata'
      ],
      color: '#455a64'
    }
  ];

  return (
    <LandingLayout>
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2196F3 0%, #42A5F5 100%)' }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 6, pb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip 
            label="TUTTE LE FUNZIONALITÀ" 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white', 
              fontWeight: 700,
              mb: 3,
              px: 2,
              py: 0.5,
              border: '1px solid rgba(255,255,255,0.3)'
            }} 
          />
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              mb: 3
            }}
          >
            Una Piattaforma Completa
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontWeight: 400,
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Tutto quello che serve per gestire la tua struttura in modo professionale ed efficiente.
            Scopri nel dettaglio ogni funzionalità del nostro gestionale.
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                elevation={6}
                sx={{ 
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 12
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ 
                      color: feature.color,
                      bgcolor: `${feature.color}15`,
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>

                  <List dense>
                    {feature.details.map((detail, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ color: feature.color, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={detail} 
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontWeight: 500, color: '#333' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 8, mb: 4 }}>
          <Card sx={{ 
            p: 6, 
            bgcolor: 'rgba(255,255,255,0.15)', 
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)'
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
              Pronto a Provare Tutte Queste Funzionalità?
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 4, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
              Inizia oggi con 30 giorni di prova gratuita. Nessuna carta di credito richiesta.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => navigate('/register')}
                sx={{ 
                  bgcolor: 'white',
                  color: landingTheme.colors.primary,
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)'
                  }
                }}
              >
                Inizia Prova Gratuita
              </Button>
              <Button 
                variant="outlined"
                size="large"
                onClick={() => navigate('/demo')}
                sx={{ 
                  borderColor: 'white',
                  borderWidth: 2,
                  color: 'white',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Richiedi Demo
              </Button>
            </Stack>
          </Card>
        </Box>
      </Container>
      </Box>
    </LandingLayout>
  );
};

export default FeaturesPage;
